import Link from "next/link"
import { ArrowRight, Wrench } from "lucide-react"
import { GrassAtlasToolIllustration } from "@/components/grass-atlas/grass-atlas-tool-illustration"

interface GuideToolCalloutProps {
  href: string
  label: string
  description: string
  variant?: "default" | "grass-atlas"
  actionLabel?: string
}

// Inline tool-link card in the editorial-glass language: a frosted ds-glass
// surface with a gradient icon chip, serif label, and an "Open tool" pill.
// Replaced the bright full-bleed green gradient box during the guides
// liquid-glass refresh.
export function GuideToolCallout({
  href,
  label,
  description,
  variant = "default",
  actionLabel = "Open tool",
}: GuideToolCalloutProps) {
  const isGrassAtlas = variant === "grass-atlas"

  return (
    <Link
      href={href}
      className="group not-prose my-6 flex items-center gap-4 ds-glass rounded-2xl p-4 transition-transform duration-200 hover:-translate-y-0.5 md:p-5"
    >
      <div className={`flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl ${isGrassAtlas ? "bg-emerald-50 ring-1 ring-emerald-900/10" : "bg-linear-to-br from-green-600 to-emerald-600 text-white shadow-md"}`}>
        {isGrassAtlas ? (
          <GrassAtlasToolIllustration className="h-12 w-12" />
        ) : (
          <Wrench className="h-6 w-6" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="font-editorial block text-base leading-tight text-gray-900 md:text-lg">
          {label}
        </span>
        <span className="mt-0.5 hidden text-sm text-gray-600 sm:block">
          {description}
        </span>
      </div>
      <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-transform group-hover:translate-x-0.5">
        <span className="hidden sm:inline">{actionLabel}</span>
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </span>
    </Link>
  )
}
